import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ShoppingCart, CreditCard, ArrowRight, Truck, Shield, Tag, Info, Building2, Banknote, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { LogoSmall } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../App';
import { useCurrency } from '../contexts/CurrencyContext';
import CouponInput from '../components/CouponInput';
import AffiliateCodeInput from '../components/AffiliateCodeInput';
import CheckoutExitPopup from '../components/CheckoutExitPopup';
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import CheckPreOrderStatus from '../components/CheckPreOrderStatus';
import { calculateSalePrice } from '../utils/calculateSalePrice';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

// Payment method options based on location
const PAYMENT_METHODS = {
  credit_card: { 
    id: 'credit_card', 
    name: 'Credit Card', 
    icon: CreditCard, 
    hasFee: true,
    description: 'Visa, Mastercard, Amex'
  },
  debit_card: { 
    id: 'debit_card', 
    name: 'Debit Card', 
    icon: CreditCard, 
    hasFee: false,
    description: 'No processing fee'
  },
  ach: { 
    id: 'ach', 
    name: 'Bank Transfer (ACH)', 
    icon: Building2, 
    hasFee: false,
    description: 'US bank accounts only',
    region: 'US'
  },
  interac: { 
    id: 'interac', 
    name: 'Interac e-Transfer', 
    icon: Banknote, 
    hasFee: false,
    description: 'Canadian banks only',
    region: 'CA'
  }
};

const Cart = () => {
  const navigate = useNavigate();
  const { items, removeItem, updateQuantity, clearCart, total } = useCart();
  const { currency, formatPrice, convertPrice } = useCurrency();
  const [loading, setLoading] = useState(false);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [appliedAffiliate, setAppliedAffiliate] = useState(null);
  const [appliedStoreCredit, setAppliedStoreCredit] = useState(null);
  const [storeCreditCode, setStoreCreditCode] = useState('');
  const [validatingCredit, setValidatingCredit] = useState(false);
  const [customerInfo, setCustomerInfo] = useState({
    name: '',
    email: '',
    address: ''
  });
  const abandonedCartTracked = useRef(false);
  const trackingTimeout = useRef(null);
  
  // Product data for sale price calculation
  const [productData, setProductData] = useState({});
  
  // Payment method selection
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('credit_card');
  const [userRegion, setUserRegion] = useState(null); // 'US', 'CA', or null
  
  // Credit card surcharge settings
  const [surchargeSettings, setSurchargeSettings] = useState({
    enabled: true,
    percent: 2.9
  });
  
  // Shipping options
  const [shippingOptions, setShippingOptions] = useState([]);
  const [selectedShipping, setSelectedShipping] = useState(null);
  const [shippingLoading, setShippingLoading] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  const [freeShippingThreshold, setFreeShippingThreshold] = useState(0);

  // Fetch current product data for sale price calculation
  useEffect(() => {
    const fetchProductData = async () => {
      if (items.length === 0) return;
      
      try {
        const productIds = items.map(item => item.product_id);
        const promises = productIds.map(id => 
          axios.get(`${API_URL}/api/products/${id}`).catch(() => null)
        );
        const responses = await Promise.all(promises);
        
        const data = {};
        responses.forEach((res, index) => {
          if (res?.data) {
            data[productIds[index]] = res.data;
          }
        });
        setProductData(data);
      } catch (error) {
        console.error('Error fetching product data:', error);
      }
    };
    
    fetchProductData();
  }, [items]);

  // Helper to get the MSRP (non-sale) price for a cart item in current currency (includes add-ons)
  const getItemMSRP = (item) => {
    const product = productData[item.product_id];
    let basePrice = item.price;
    if (product) {
      basePrice = product.region_prices?.[currency] || product.price;
    }
    
    // Include add-ons in the MSRP
    const addonsTotal = (item.selectedAddons || []).reduce((sum, addon) => {
      const addonPrice = currency === 'CAD' ? (addon.price_cad || addon.price) : addon.price;
      return sum + (addonPrice * (addon.quantity || 1));
    }, 0);
    
    return basePrice + addonsTotal;
  };
  
  // Helper to get the effective price for a cart item (currency-specific, sale price if applicable, includes add-ons)
  const getItemPrice = (item) => {
    const product = productData[item.product_id];
    let basePrice = item.price;
    
    if (product) {
      const saleInfo = calculateSalePrice(product, currency);
      const itemMSRP = product.region_prices?.[currency] || product.price;
      basePrice = saleInfo?.isOnSale ? saleInfo.effectivePrice : itemMSRP;
    }
    
    // Add add-ons total
    const addonsTotal = (item.selectedAddons || []).reduce((sum, addon) => {
      const addonPrice = currency === 'CAD' ? (addon.price_cad || addon.price) : addon.price;
      return sum + (addonPrice * (addon.quantity || 1));
    }, 0);
    
    return basePrice + addonsTotal;
  };

  // Calculate actual total with sale prices
  const calculateActualTotal = () => {
    return items.reduce((sum, item) => {
      return sum + getItemPrice(item) * item.quantity;
    }, 0);
  };

  const actualTotal = Object.keys(productData).length > 0 ? calculateActualTotal() : total;

  // Calculate MSRP total (original prices, no sale discounts) for shipping threshold checks
  const msrpTotal = useMemo(() => {
    return items.reduce((sum, item) => {
      return sum + getItemMSRP(item) * item.quantity;
    }, 0);
  }, [items, productData, currency]);

  // Detect user region for payment methods
  useEffect(() => {
    const detectRegion = async () => {
      try {
        // First check if currency is CAD - if so, assume Canadian user
        if (currency === 'CAD') {
          setUserRegion('CA');
          return;
        }
        
        // Use timezone as a region indicator
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (timezone.includes('America/Toronto') || timezone.includes('America/Vancouver') || 
            timezone.includes('America/Montreal') || timezone.includes('Canada') ||
            timezone.includes('America/Edmonton') || timezone.includes('America/Winnipeg') ||
            timezone.includes('America/Halifax') || timezone.includes('America/St_Johns')) {
          setUserRegion('CA');
        } else if (timezone.includes('America/') && !timezone.includes('America/Mexico')) {
          setUserRegion('US');
        } else {
          // Try IP-based detection as fallback
          try {
            const res = await axios.get(`${API_URL}/api/geo/detect`);
            setUserRegion(res.data.country_code);
          } catch {
            // Default to showing both options
            setUserRegion(null);
          }
        }
      } catch (error) {
        // Default to showing both options
        setUserRegion(null);
      }
    };
    detectRegion();
  }, [currency]);

  // Tax settings (populated from /api/settings)
  const [taxSettings, setTaxSettings] = useState({
    enabled: true,
    label: 'GST',
    rates: {},
    default_percent: 0,
  });

  // Fetch surcharge settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/settings`);
        setSurchargeSettings({
          enabled: res.data.credit_card_surcharge_enabled ?? true,
          percent: res.data.credit_card_surcharge_percent ?? 2.9
        });
        setTaxSettings({
          enabled: res.data.tax_enabled ?? true,
          label: res.data.tax_label || 'GST',
          rates: res.data.tax_rates || {},
          default_percent: res.data.tax_default_percent ?? 0,
        });
      } catch (error) {
        // Use defaults
      }
    };
    fetchSettings();
  }, []);

  // Get available payment methods based on region
  const getAvailablePaymentMethods = () => {
    const methods = ['credit_card', 'debit_card'];
    if (userRegion === 'CA' || userRegion === null) {
      methods.push('interac');
    }
    if (userRegion === 'US' || userRegion === null) {
      methods.push('ach');
    }
    return methods.map(id => PAYMENT_METHODS[id]);
  };

  // Check if current payment method has surcharge
  const hasSurcharge = selectedPaymentMethod === 'credit_card' && surchargeSettings.enabled;

  // Fetch shipping options based on postal code
  const fetchShippingOptions = async (postal) => {
    if (!postal || postal.length < 3) return;
    
    setShippingLoading(true);
    try {
      const cartItems = items.map(item => ({
        product_id: item.product_id,
        quantity: item.quantity
      }));
      
      const res = await axios.post(`${API_URL}/api/shipping/estimate-cart`, {
        postal_code: postal,
        items: cartItems
      });
      
      setShippingOptions(res.data.options || []);
      setFreeShippingThreshold(res.data.free_shipping_threshold || 0);
      
      // Auto-select cheapest option if none selected
      if (res.data.options?.length > 0 && !selectedShipping) {
        setSelectedShipping(res.data.options[0]);
      }
    } catch (error) {
      console.error('Failed to fetch shipping:', error);
      // Set default shipping option
      setShippingOptions([{
        method: 'standard',
        name: 'Standard Shipping',
        cost: 0,
        days_min: 5,
        days_max: 10
      }]);
    } finally {
      setShippingLoading(false);
    }
  };

  // Self-serve shipping fallbacks shown even when no postal code is entered yet.
  // Customers can pick local pickup / arrange own / quote later without a quote.
  const SELF_SERVE_OPTIONS = [
    { method: 'local_pickup', name: 'Local Pickup', cost: 0, days_min: 0, days_max: 0, self_serve: true,
      description: "Pick up your order in person. Our team will email you to arrange a time once payment is complete." },
    { method: 'arrange_own', name: 'Arrange My Own Shipping', cost: 0, days_min: 0, days_max: 0, self_serve: true,
      description: "Use your own courier or freight carrier. We'll prepare the order for pickup at our warehouse." },
    { method: 'quote_later', name: 'Quote Shipping Later', cost: 0, days_min: 0, days_max: 0, self_serve: true,
      description: "Pay for the product now; we'll contact you with a separate shipping invoice within 1 business day." },
  ];

  // Fetch shipping when postal code changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (postalCode && postalCode.length >= 3) {
        fetchShippingOptions(postalCode);
      } else {
        // No postal code yet — still offer the self-serve alternatives
        setShippingOptions(SELF_SERVE_OPTIONS);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [postalCode, items]);

  // Calculate shipping cost
  const shippingCost = useMemo(() => {
    const subtotal = items.reduce((sum, item) => {
      const price = productData[item.product_id]?.price || item.price;
      return sum + (price * item.quantity);
    }, 0);
    
    // Free shipping if above threshold
    if (freeShippingThreshold > 0 && subtotal >= freeShippingThreshold) {
      return 0;
    }
    
    return selectedShipping?.cost || 0;
  }, [selectedShipping, items, productData, freeShippingThreshold]);

  // Handle discount code from exit popup
  const handleApplyExitDiscount = (code) => {
    // Apply the discount code
    toast.success(`Discount code ${code} applied!`);
    // You could also automatically apply the coupon here
  };

  // Check for store credit in URL params
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const creditCode = urlParams.get('credit');
    if (creditCode && !appliedStoreCredit) {
      setStoreCreditCode(creditCode);
      handleApplyStoreCredit(creditCode);
    }
  }, []);

  // Apply store credit
  const handleApplyStoreCredit = async (code = storeCreditCode) => {
    if (!code.trim()) {
      toast.error('Please enter a store credit code');
      return;
    }
    
    setValidatingCredit(true);
    try {
      const response = await axios.get(`${API_URL}/api/store-credits/validate/${code.toUpperCase()}`);
      if (response.data.valid) {
        setAppliedStoreCredit({
          code: response.data.code,
          remainingBalance: response.data.remaining_balance,
          originalAmount: response.data.original_amount,
          referrerName: response.data.referrer_name
        });
        toast.success(`Store credit of $${response.data.remaining_balance.toFixed(2)} applied!`);
        setStoreCreditCode('');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid or expired store credit code');
      setAppliedStoreCredit(null);
    } finally {
      setValidatingCredit(false);
    }
  };

  // Remove store credit
  const handleRemoveStoreCredit = () => {
    setAppliedStoreCredit(null);
    setStoreCreditCode('');
    toast.success('Store credit removed');
  };

  // Track which discount source is selected (customer choice - no stacking)
  // Options: 'sale' (product on sale), 'coupon', 'referral', 'affiliate', 'none'
  const [selectedDiscountSource, setSelectedDiscountSource] = useState('none');
  const [availableDiscounts, setAvailableDiscounts] = useState([]);
  
  // Check for referral/affiliate code in cookies or session
  const [referralDiscount, setReferralDiscount] = useState(null);
  
  useEffect(() => {
    // Check for referral code, affiliate code, or campaign code
    const refCode = localStorage.getItem('referral_code');
    const affCode = localStorage.getItem('affiliate_code');
    const campaignCode = localStorage.getItem('campaign_code');
    
    if (refCode || affCode || campaignCode) {
      // Fetch referral/affiliate discount details
      const fetchReferralDiscount = async () => {
        try {
          const code = refCode || affCode || campaignCode;
          const type = refCode ? 'referral' : 'affiliate';
          
          // Try to validate the code to get actual discount
          let discountPercent = 5; // Default
          try {
            const validateRes = await axios.get(`${API_URL}/api/affiliates/validate?code=${code}`);
            if (validateRes.data.valid) {
              discountPercent = validateRes.data.customer_discount_percent || 5;
            }
          } catch (e) {
            // Fall back to settings-based discount
            const res = await axios.get(`${API_URL}/api/settings`);
            discountPercent = type === 'referral' 
              ? res.data.referral_discount_percent || 5
              : res.data.affiliate_customer_discount || 5;
          }
          
          setReferralDiscount({
            type,
            code,
            campaignCode: campaignCode || null,
            percent: discountPercent,
            amount: total * (discountPercent / 100)
          });
        } catch (e) {
          // Silent fail
        }
      };
      fetchReferralDiscount();
    }
  }, [total]);

  // Build list of available discounts
  useEffect(() => {
    const discounts = [];
    
    // Get MSRP total in current currency (original prices, no sale discounts)
    const msrpTotal = items.reduce((sum, item) => {
      return sum + getItemMSRP(item) * item.quantity;
    }, 0);
    
    // Check if any items are on sale - calculate savings from sale prices
    const saleItems = items.filter(item => {
      const product = productData[item.product_id];
      if (!product) return false;
      const saleInfo = calculateSalePrice(product, currency);
      return saleInfo.isOnSale;
    });
    
    // Calculate sale discount amount
    let saleDiscount = 0;
    if (saleItems.length > 0) {
      saleDiscount = items.reduce((sum, item) => {
        const product = productData[item.product_id];
        if (!product) return sum;
        const saleInfo = calculateSalePrice(product, currency);
        const itemMSRP = getItemMSRP(item);
        if (saleInfo.isOnSale && saleInfo.effectivePrice) {
          return sum + ((itemMSRP - saleInfo.effectivePrice) * item.quantity);
        }
        return sum;
      }, 0);
      
      discounts.push({
        source: 'sale',
        label: 'Sale Price',
        description: `${saleItems.length} item(s) on sale`,
        amount: saleDiscount,
        finalPrice: msrpTotal - saleDiscount,
        autoApplied: true
      });
    }
    
    // Calculate actual subtotal of NON-sale items (for coupon application)
    const nonSaleItemsTotal = items.reduce((sum, item) => {
      const product = productData[item.product_id];
      const itemMSRP = getItemMSRP(item);
      if (!product) return sum + itemMSRP * item.quantity;
      const saleInfo = calculateSalePrice(product, currency);
      if (!saleInfo.isOnSale) {
        return sum + itemMSRP * item.quantity;
      }
      return sum;
    }, 0);
    
    // Check if coupon is applied - ONLY applies to non-sale items
    if (appliedCoupon) {
      // Handle referral codes entered through CouponInput
      if (appliedCoupon.code_type === 'referral') {
        const referralPercent = appliedCoupon.discount_percent || 5;
        const referralDiscountAmount = msrpTotal * (referralPercent / 100);
        const referralFinalPrice = msrpTotal - referralDiscountAmount;
        const saleFinalPrice = msrpTotal - saleDiscount;
        const isBetterThanSale = referralFinalPrice < saleFinalPrice;
        
        discounts.push({
          source: 'referral',
          label: `Refer-a-Friend: ${appliedCoupon.referrer_name || 'Friend'}`,
          description: isBetterThanSale 
            ? `${referralPercent}% off MSRP - Best savings!`
            : `${referralPercent}% off MSRP`,
          amount: referralDiscountAmount,
          finalPrice: referralFinalPrice,
          overridesSale: true,
          isBetterThanSale: isBetterThanSale,
          referralCode: appliedCoupon.referral_code
        });
      } else {
        // Handle regular coupon codes
        let couponDiscount = 0;
        const coupon = appliedCoupon.coupon;
        
        if (nonSaleItemsTotal > 0 && coupon) {
          if (coupon.discount_type === 'percentage') {
            couponDiscount = nonSaleItemsTotal * (coupon.discount_value / 100);
          } else if (coupon.discount_type === 'fixed') {
            couponDiscount = Math.min(coupon.discount_value, nonSaleItemsTotal);
          }
        }
        
        if (couponDiscount > 0) {
          discounts.push({
            source: 'coupon',
            label: `Coupon (${coupon?.code})`,
            description: saleItems.length > 0 
              ? `Applied to ${items.length - saleItems.length} non-sale item(s)` 
              : (coupon?.description || 'Promo code discount'),
            amount: couponDiscount,
            finalPrice: msrpTotal - couponDiscount,
            isExclusive: coupon?.is_exclusive !== false
          });
        } else if (saleItems.length > 0 && saleItems.length === items.length) {
          toast.info('Coupon cannot be applied - all items are already on sale');
          setAppliedCoupon(null);
        }
      }
    }
    
    // Check if referral/affiliate code exists (from localStorage - visited via affiliate link)
    if (referralDiscount) {
      const affiliateDiscountAmount = msrpTotal * (referralDiscount.percent / 100);
      const affiliateFinalPrice = msrpTotal - affiliateDiscountAmount;
      const saleFinalPrice = msrpTotal - saleDiscount;
      const isBetterThanSale = affiliateFinalPrice < saleFinalPrice;
      
      discounts.push({
        source: referralDiscount.type,
        label: referralDiscount.type === 'referral' ? 'Refer-a-Friend' : 'Affiliate Discount',
        description: isBetterThanSale 
          ? `${referralDiscount.percent}% off MSRP - Best savings!`
          : `${referralDiscount.percent}% off MSRP - Support your affiliate`,
        amount: affiliateDiscountAmount,
        finalPrice: affiliateFinalPrice,
        overridesSale: true,
        isBetterThanSale: isBetterThanSale,
        affiliateName: referralDiscount.code
      });
    }
    
    // Check if manually entered affiliate code exists
    if (appliedAffiliate && appliedAffiliate.customer_discount_percent > 0) {
      const affiliatePercent = appliedAffiliate.customer_discount_percent;
      const affiliateDiscountAmount = msrpTotal * (affiliatePercent / 100);
      const affiliateFinalPrice = msrpTotal - affiliateDiscountAmount;
      const saleFinalPrice = msrpTotal - saleDiscount;
      const isBetterThanSale = affiliateFinalPrice < saleFinalPrice;
      
      // Don't add duplicate if referralDiscount already exists with same code
      const existingAffiliate = discounts.find(d => d.source === 'affiliate' || d.source === 'referral');
      if (!existingAffiliate) {
        discounts.push({
          source: 'affiliate',
          label: `Affiliate: ${appliedAffiliate.affiliate_name}`,
          description: isBetterThanSale 
            ? `${affiliatePercent}% off MSRP - Best savings!`
            : `${affiliatePercent}% off MSRP - Support ${appliedAffiliate.affiliate_name}`,
          amount: affiliateDiscountAmount,
          finalPrice: affiliateFinalPrice,
          overridesSale: true,
          isBetterThanSale: isBetterThanSale,
          affiliateName: appliedAffiliate.affiliate_name
        });
      }
    }
    
    setAvailableDiscounts(discounts);
    
    // CUSTOMER CHOICE: Don't auto-select - let the customer choose their discount
    // Only auto-select if there's exactly one discount option, or no affiliate/sale conflict
    if (discounts.length === 1) {
      setSelectedDiscountSource(discounts[0].source);
    } else if (discounts.length > 1 && selectedDiscountSource === 'none') {
      // If no selection made yet, default to the best discount but allow changing
      let bestDiscount = discounts[0];
      for (const d of discounts) {
        if (d.finalPrice < bestDiscount.finalPrice) {
          bestDiscount = d;
        }
      }
      setSelectedDiscountSource(bestDiscount.source);
    }
    // If user has already made a selection, keep it
  }, [items, appliedCoupon, referralDiscount, appliedAffiliate, productData, currency, total]);

  // Track abandoned cart when user enters email and has items
  const trackAbandonedCart = useCallback(async () => {
    if (!customerInfo.email || items.length === 0) return;
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerInfo.email)) return;

    try {
      await axios.post(`${API_URL}/api/emails/abandoned-cart/track`, {
        email: customerInfo.email,
        customer_name: customerInfo.name || null,
        total: total,
        items: items.map(i => ({
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          image_url: i.image_url
        }))
      });
      abandonedCartTracked.current = true;
    } catch (err) {
      // Silent fail - don't interrupt user experience
      console.log('Abandoned cart tracking:', err.response?.data || err.message);
    }
  }, [customerInfo.email, customerInfo.name, items, total]);

  // Debounced tracking - triggers 5 seconds after user stops typing email
  useEffect(() => {
    if (trackingTimeout.current) {
      clearTimeout(trackingTimeout.current);
    }

    if (customerInfo.email && items.length > 0) {
      trackingTimeout.current = setTimeout(() => {
        trackAbandonedCart();
      }, 5000); // 5 second delay
    }

    return () => {
      if (trackingTimeout.current) {
        clearTimeout(trackingTimeout.current);
      }
    };
  }, [customerInfo.email, items, trackAbandonedCart]);

  const productIds = items.map(i => i.product_id);
  
  // Calculate discount based on selected source
  // Now uses finalPrice from the discount to ensure proper comparison
  const getActiveDiscount = () => {
    const activeDiscount = availableDiscounts.find(d => d.source === selectedDiscountSource);
    return activeDiscount || null;
  };
  
  const activeDiscount = getActiveDiscount();
  
  // Calculate the final price based on selected discount
  // If affiliate/referral is selected, use MSRP minus affiliate discount
  // If sale is selected, use actualTotal (sale prices already applied)
  // If no discount or coupon, use actualTotal
  const calculateFinalSubtotal = () => {
    if (!activeDiscount) return actualTotal;
    
    if (activeDiscount.overridesSale) {
      // Affiliate/referral discount - calculated from MSRP
      return activeDiscount.finalPrice;
    } else if (activeDiscount.source === 'sale') {
      // Sale discount - use actualTotal (already has sale prices)
      return actualTotal;
    } else if (activeDiscount.source === 'coupon') {
      // Coupon - applies to non-sale items, so subtract from actualTotal
      return actualTotal - activeDiscount.amount;
    }
    
    return actualTotal;
  };
  
  const subtotalAfterDiscount = Math.max(0, calculateFinalSubtotal());
  const discountAmount = total - subtotalAfterDiscount; // Total savings from MSRP

  // Resolve the tax rate for the customer's region (mirrors backend logic).
  // CAD orders look up `CA-XX` where XX is the province inferred from the
  // postal-code first letter; USD orders look up `US`. Falls back to country
  // code, then to default.
  const provinceFromPostal = (p) => {
    if (!p) return '';
    const c = p.trim().toUpperCase().charAt(0);
    return ({ A:'NL', B:'NS', C:'PE', E:'NB', G:'QC', H:'QC', J:'QC',
              K:'ON', L:'ON', M:'ON', N:'ON', P:'ON',
              R:'MB', S:'SK', T:'AB', V:'BC', X:'NT', Y:'YT' })[c] || '';
  };
  const taxCountry = currency === 'CAD' ? 'CA' : (currency === 'USD' ? 'US' : '');
  const taxProvince = taxCountry === 'CA' ? provinceFromPostal(postalCode) : '';
  const taxPercent = taxSettings.enabled
    ? (
        (taxProvince && taxSettings.rates?.[`${taxCountry}-${taxProvince}`])
        ?? taxSettings.rates?.[taxCountry]
        ?? taxSettings.default_percent
        ?? 0
      )
    : 0;
  const taxAmount = taxPercent > 0
    ? Math.round(subtotalAfterDiscount * (taxPercent / 100) * 100) / 100
    : 0;

  // Calculate credit card surcharge (only for credit card payments)
  const surchargeAmount = hasSurcharge
    ? Math.round(subtotalAfterDiscount * (surchargeSettings.percent / 100) * 100) / 100
    : 0;

  // Calculate store credit application
  const storeCreditAmount = appliedStoreCredit
    ? Math.min(appliedStoreCredit.remainingBalance, subtotalAfterDiscount + taxAmount + surchargeAmount + shippingCost)
    : 0;

  const finalTotal = Math.max(0, subtotalAfterDiscount + taxAmount + surchargeAmount + shippingCost - storeCreditAmount);

  // Calculate savings from sale prices
  const totalSavings = total - actualTotal;

  const handleApplyCoupon = (couponData) => {
    setAppliedCoupon(couponData);
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
  };

  const handleCheckout = async () => {
    if (!customerInfo.name || !customerInfo.email || !customerInfo.address) {
      toast.error('Please fill in all customer information');
      return;
    }

    if (items.length === 0) {
      toast.error('Your cart is empty');
      return;
    }

    setLoading(true);
    try {
      // Store cart items in sessionStorage before checkout for order confirmation display
      sessionStorage.setItem('checkout_cart_items', JSON.stringify(items));
      sessionStorage.setItem('checkout_customer_info', JSON.stringify(customerInfo));
      sessionStorage.setItem('checkout_discount_info', JSON.stringify({
        appliedAffiliate,
        appliedCoupon: appliedCoupon?.coupon,
        appliedStoreCredit,
        selectedDiscountSource,
        activeDiscount,
        currency
      }));
      
      const orderData = {
        items: items.map(i => ({
          product_id: i.product_id,
          name: i.name,
          price: i.price,
          quantity: i.quantity,
          configuration: i.configuration || null
        })),
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        shipping_address: customerInfo.address,
        origin_url: window.location.origin,
        payment_method: selectedPaymentMethod,
        currency: currency,
        // Shipping info
        shipping_method: selectedShipping?.method || 'standard',
        shipping_cost: shippingCost || 0,
        postal_code: postalCode || '',
        // Store credit info
        store_credit_code: appliedStoreCredit?.code || null,
        store_credit_amount: storeCreditAmount || 0,
        // Only ONE discount code can be applied (no stacking)
        // Priority: affiliate_code > referral_code > coupon_code > sale price
        // Include affiliate from either manual entry OR from localStorage (referral link visit)
        campaign_code: appliedAffiliate?.campaign_code || referralDiscount?.campaignCode || localStorage.getItem('campaign_code') || null,
        affiliate_code: appliedAffiliate?.affiliate_code || referralDiscount?.code || null,
        // Include referral code from CouponInput if applied
        referral_code: appliedCoupon?.code_type === 'referral' ? appliedCoupon.referral_code : null,
        referral_discount_percent: appliedCoupon?.code_type === 'referral' ? appliedCoupon.discount_percent : null,
        coupon_code: (!(appliedAffiliate?.affiliate_code || referralDiscount?.code) && appliedCoupon?.code_type !== 'referral' && appliedCoupon?.coupon?.code) ? appliedCoupon.coupon.code : null,
        // Safety: send the exact total shown to the customer. Backend will
        // refuse to create the Stripe session if its own computation disagrees
        // by more than $0.01 — prevents the customer ever seeing a different
        // price on the Stripe page.
        expected_total: Number(finalTotal.toFixed(2))
      };

      const res = await axios.post(`${API_URL}/api/orders/checkout`, orderData);
      
      if (res.data.checkout_url) {
        // Record coupon usage if applied
        if (appliedCoupon?.coupon?.id) {
          const token = localStorage.getItem('token');
          const userId = token ? JSON.parse(atob(token.split('.')[1])).sub : null;
          await axios.post(`${API_URL}/api/coupons/${appliedCoupon.coupon.id}/use`, {
            user_id: userId,
            order_id: res.data.order_id
          }).catch(() => {}); // Silent fail
        }

        // Store session ID for the success page
        sessionStorage.setItem('checkout_session_id', res.data.session_id);

        // Redirect the current tab to Stripe Checkout. We deliberately do NOT
        // clear the cart here — if the customer hits "back" or closes the
        // Stripe tab without paying, their items are still waiting. The cart
        // is only cleared on the success page once Stripe confirms `paid`.
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      console.error('Checkout error:', e);
      // Surface a meaningful message for price-parity mismatches and other
      // structured API errors; fall back to the generic toast otherwise.
      const apiDetail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && typeof apiDetail === 'string') {
        toast.error(apiDetail);
      } else if (typeof apiDetail === 'string') {
        toast.error(apiDetail);
      } else {
        toast.error('Failed to create checkout session');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f0f4f8]">
      <Navbar />

      <div className="pt-44 pb-16 section-padding">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-4xl font-bold text-[#0a0203] mb-8" style={{  }}>
            Shopping Cart
          </h1>

          {items.length === 0 ? (
            <div className="space-y-6">
              <div className="text-center py-20 bg-white rounded-2xl">
                <ShoppingCart className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <h2 className="text-2xl font-semibold text-[#0a0203] mb-2" style={{  }}>
                  Your cart is empty
                </h2>
                <p className="text-gray-500 mb-8">Looks like you haven't added any items yet.</p>
                <Link to="/products">
                  <Button className="bg-[#003456] hover:bg-[#001d30] text-white btn-scale" data-testid="continue-shopping-btn">
                    Continue Shopping
                  </Button>
                </Link>
              </div>
              
              {/* Pre-Order Status Checker - Available even with empty cart */}
              <div className="max-w-xl mx-auto">
                <CheckPreOrderStatus />
              </div>
            </div>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map((item) => {
                  const product = productData[item.product_id];
                  const saleInfo = product ? calculateSalePrice(product, currency) : null;
                  // Get currency-specific MSRP
                  const itemMSRP = product?.region_prices?.[currency] || product?.price || item.price;
                  const effectivePrice = saleInfo?.isOnSale ? saleInfo.effectivePrice : itemMSRP;
                  const isOnSale = saleInfo?.isOnSale && saleInfo.effectivePrice < itemMSRP;
                  
                  // Calculate add-ons total for display
                  const addonsTotal = (item.selectedAddons || []).reduce((sum, addon) => {
                    const addonPrice = currency === 'CAD' ? (addon.price_cad || addon.price) : addon.price;
                    return sum + (addonPrice * (addon.quantity || 1));
                  }, 0);
                  
                  const itemTotalPrice = effectivePrice + addonsTotal;
                  const cartItemId = item.cart_item_id || item.product_id;
                  
                  return (
                  <div key={cartItemId} className="bg-white rounded-2xl p-6 flex gap-6 animate-fade-in" data-testid={`cart-item-${item.product_id}`}>
                    <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-[#0a0203]" style={{  }}>{item.name}</h3>
                          {isOnSale ? (
                            <div className="mt-1">
                              <div className="flex items-center gap-2">
                                <p className="text-emerald-600 font-bold" style={{ fontFamily: 'JetBrains Mono' }}>
                                  {formatPrice(effectivePrice)}
                                </p>
                                <p className="text-gray-400 line-through text-sm" style={{ fontFamily: 'JetBrains Mono' }}>
                                  {formatPrice(itemMSRP)}
                                </p>
                                <span className="bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded text-xs font-bold">
                                  SALE
                                </span>
                              </div>
                            </div>
                          ) : (
                            <p className="text-[#003456] font-bold mt-1" style={{ fontFamily: 'JetBrains Mono' }}>
                              {formatPrice(effectivePrice)}
                            </p>
                          )}
                          
                          {/* Display selected add-ons */}
                          {item.selectedAddons && item.selectedAddons.length > 0 && (
                            <div className="mt-2 space-y-1">
                              {item.selectedAddons.map((addon, idx) => {
                                const addonPrice = currency === 'CAD' ? (addon.price_cad || addon.price) : addon.price;
                                return (
                                  <div key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                                    <span className="text-green-600">+</span>
                                    <span>{addon.name}</span>
                                    {addon.quantity > 1 && <span className="text-gray-400">×{addon.quantity}</span>}
                                    <span className="text-green-600 font-medium">
                                      {formatPrice(addonPrice * (addon.quantity || 1))}
                                    </span>
                                  </div>
                                );
                              })}
                              {addonsTotal > 0 && (
                                <div className="pt-1 border-t border-gray-100 mt-1">
                                  <span className="text-sm font-medium text-gray-700">
                                    Item Total: {formatPrice(itemTotalPrice)}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <button
                          onClick={() => removeItem(cartItemId)}
                          className="text-gray-400 hover:text-[#fdc31c] transition-colors"
                          data-testid={`remove-item-${item.product_id}`}
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>
                      <div className="flex items-center gap-3 mt-4">
                        <button
                          onClick={() => updateQuantity(cartItemId, item.quantity - 1)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                          data-testid={`decrease-${item.product_id}`}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <span className="font-medium">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(cartItemId, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                          data-testid={`increase-${item.product_id}`}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    <div className="text-right">
                      {isOnSale ? (
                        <div>
                          <p className="font-bold text-emerald-600" style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatPrice(itemTotalPrice * item.quantity)}
                          </p>
                          <p className="text-xs text-gray-400 line-through" style={{ fontFamily: 'JetBrains Mono' }}>
                            {formatPrice((itemMSRP + addonsTotal) * item.quantity)}
                          </p>
                        </div>
                      ) : (
                        <p className="font-bold text-[#0a0203]" style={{ fontFamily: 'JetBrains Mono' }}>
                          {formatPrice(itemTotalPrice * item.quantity)}
                        </p>
                      )}
                    </div>
                  </div>
                  );
                })}

                <button
                  onClick={clearCart}
                  className="text-gray-500 hover:text-[#fdc31c] text-sm font-medium transition-colors"
                  data-testid="clear-cart-btn"
                >
                  Clear Cart
                </button>

                {/* Pre-Order Status Checker */}
                <div className="mt-6">
                  <CheckPreOrderStatus />
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="bg-white rounded-2xl p-6 sticky top-28">
                  <h2 className="text-xl font-bold text-[#0a0203] mb-6" style={{  }}>
                    Order Summary
                  </h2>

                  {/* Customer Info */}
                  <div className="space-y-4 mb-6">
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Full Name</label>
                      <Input
                        value={customerInfo.name}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                        placeholder="John Doe"
                        className="input-focus"
                        data-testid="customer-name-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Email</label>
                      <Input
                        type="email"
                        value={customerInfo.email}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                        placeholder="john@example.com"
                        className="input-focus"
                        data-testid="customer-email-input"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-gray-600 mb-1 block">Shipping Address</label>
                      <Input
                        value={customerInfo.address}
                        onChange={(e) => setCustomerInfo({ ...customerInfo, address: e.target.value })}
                        placeholder="123 Main St, City, State, ZIP"
                        className="input-focus"
                        data-testid="customer-address-input"
                      />
                    </div>
                  </div>

                  {/* Coupon Code */}
                  <div className="border-t border-gray-100 pt-4 mb-4">
                    <label className="text-sm text-gray-600 mb-2 block">Promo Code</label>
                    <CouponInput 
                      cartTotal={total}
                      productIds={productIds}
                      onApply={handleApplyCoupon}
                      onRemove={handleRemoveCoupon}
                      appliedCoupon={appliedCoupon}
                    />
                  </div>

                  {/* Affiliate/Campaign Code */}
                  <div className="border-t border-gray-100 pt-4 mb-4">
                    <label className="text-sm text-gray-600 mb-2 block">Affiliate/Partner Code</label>
                    {appliedAffiliate ? (
                      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-emerald-800">{appliedAffiliate.affiliate_name}</p>
                            <p className="text-sm text-emerald-600">
                              {appliedAffiliate.customer_discount_percent > 0 
                                ? `${appliedAffiliate.customer_discount_percent}% off MSRP` 
                                : 'Code applied'}
                            </p>
                            {appliedAffiliate.is_campaign && (
                              <p className="text-xs text-emerald-500 mt-1">Campaign: {appliedAffiliate.campaign_name}</p>
                            )}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setAppliedAffiliate(null)}
                            className="text-emerald-600 hover:text-emerald-800"
                          >
                            Remove
                          </Button>
                        </div>
                        {(() => {
                          const affiliateDiscount = availableDiscounts.find(d => d.source === 'affiliate' || d.source === 'referral');
                          const saleDiscount = availableDiscounts.find(d => d.source === 'sale');
                          
                          if (affiliateDiscount && saleDiscount) {
                            if (affiliateDiscount.isBetterThanSale) {
                              return (
                                <p className="text-xs text-emerald-600 mt-2 font-medium">
                                  ✓ Your affiliate discount saves you more than the current sale!
                                </p>
                              );
                            } else {
                              return (
                                <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
                                  <p className="text-amber-700 font-medium">
                                    The sale price saves more, but you can still support {appliedAffiliate.affiliate_name}!
                                  </p>
                                  <p className="text-amber-600 mt-1">
                                    Use the "Choose Your Discount" selector below to pick which discount you prefer.
                                  </p>
                                </div>
                              );
                            }
                          }
                          return null;
                        })()}
                      </div>
                    ) : (
                      <AffiliateCodeInput 
                        cartTotal={total}
                        onApply={(data) => setAppliedAffiliate(data)}
                      />
                    )}
                  </div>

                  {/* Store Credit */}
                  <div className="border-t border-gray-100 pt-4 mb-4">
                    <label className="text-sm text-gray-600 mb-2 block">Store Credit</label>
                    {appliedStoreCredit ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <div>
                            <p className="font-medium text-blue-800">
                              Credit Applied: ${appliedStoreCredit.remainingBalance.toFixed(2)}
                            </p>
                            <p className="text-sm text-blue-600">
                              Code: {appliedStoreCredit.code}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveStoreCredit}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter store credit code"
                          value={storeCreditCode}
                          onChange={(e) => setStoreCreditCode(e.target.value.toUpperCase())}
                          className="flex-1"
                          data-testid="store-credit-input"
                        />
                        <Button
                          onClick={() => handleApplyStoreCredit()}
                          disabled={validatingCredit || !storeCreditCode.trim()}
                          variant="outline"
                          data-testid="apply-store-credit-button"
                        >
                          {validatingCredit ? 'Checking...' : 'Apply'}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Discount Selection (when multiple discounts available) */}
                  {availableDiscounts.length > 1 && (
                    <div className="border-t border-gray-100 pt-4 mb-4">
                      <label className="text-sm text-gray-600 mb-2 block font-medium flex items-center gap-2">
                        <Tag className="h-4 w-4" />
                        Choose Your Discount
                      </label>
                      <p className="text-xs text-gray-500 mb-3">Only one discount can be applied per order. You choose which to use!</p>
                      <div className="space-y-2">
                        {availableDiscounts.map((discount) => {
                          const isAffiliate = discount.source === 'affiliate' || discount.source === 'referral';
                          const isBest = discount.isBetterThanSale;
                          
                          return (
                          <label 
                            key={discount.source}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              selectedDiscountSource === discount.source
                                ? isAffiliate ? 'border-emerald-500 bg-emerald-50' : 'border-green-500 bg-green-50'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid={`discount-option-${discount.source}`}
                          >
                            <input
                              type="radio"
                              name="discountSource"
                              value={discount.source}
                              checked={selectedDiscountSource === discount.source}
                              onChange={() => setSelectedDiscountSource(discount.source)}
                              className="sr-only"
                            />
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              selectedDiscountSource === discount.source
                                ? isAffiliate ? 'border-emerald-500 bg-emerald-500' : 'border-green-500 bg-green-500'
                                : 'border-gray-300'
                            }`}>
                              {selectedDiscountSource === discount.source && (
                                <div className="w-2 h-2 rounded-full bg-white" />
                              )}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-900">{discount.label}</span>
                                  {isBest && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-yellow-100 text-yellow-800 rounded-full font-semibold">
                                      BEST SAVINGS
                                    </span>
                                  )}
                                  {isAffiliate && !isBest && (
                                    <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-800 rounded-full font-semibold">
                                      SUPPORT AFFILIATE
                                    </span>
                                  )}
                                </div>
                                <span className={`font-bold ${isAffiliate ? 'text-emerald-600' : 'text-green-600'}`}>
                                  -${discount.amount.toFixed(2)}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500">{discount.description}</span>
                              {isAffiliate && !isBest && (
                                <p className="text-[10px] text-emerald-600 mt-1 italic">
                                  Choose this to support your affiliate partner
                                </p>
                              )}
                            </div>
                          </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Payment Method Selection */}
                  <div className="border-t border-gray-100 pt-4 mb-4">
                    <label className="text-sm text-gray-600 mb-3 block font-medium">Payment Method</label>
                    <div className="space-y-2">
                      {getAvailablePaymentMethods().map((method) => {
                        const Icon = method.icon;
                        const isSelected = selectedPaymentMethod === method.id;
                        return (
                          <label 
                            key={method.id}
                            className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                              isSelected 
                                ? 'border-[#003456] bg-[#003456]/5' 
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            data-testid={`payment-method-${method.id}`}
                          >
                            <input
                              type="radio"
                              name="paymentMethod"
                              value={method.id}
                              checked={isSelected}
                              onChange={() => setSelectedPaymentMethod(method.id)}
                              className="sr-only"
                            />
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              isSelected ? 'bg-[#003456] text-white' : 'bg-gray-100 text-gray-600'
                            }`}>
                              <Icon className="h-5 w-5" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between">
                                <span className={`font-medium ${isSelected ? 'text-[#003456]' : 'text-gray-900'}`}>
                                  {method.name}
                                </span>
                                {method.hasFee && surchargeSettings.enabled && (
                                  <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                    +{surchargeSettings.percent}% fee
                                  </span>
                                )}
                                {!method.hasFee && (
                                  <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">
                                    No fee
                                  </span>
                                )}
                              </div>
                              <span className="text-xs text-gray-500">{method.description}</span>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div className="border-t border-gray-100 pt-4 space-y-2">
                    {/* Subtotal - show MSRP when affiliate is selected, sale price otherwise */}
                    {(() => {
                      const isAffiliateSelected = selectedDiscountSource === 'affiliate' || selectedDiscountSource === 'referral';
                      const showMSRP = isAffiliateSelected && activeDiscount?.overridesSale;
                      // Calculate currency-specific MSRP total
                      const currencyMSRP = items.reduce((sum, item) => {
                        const product = productData[item.product_id];
                        const itemMSRP = product?.region_prices?.[currency] || product?.price || item.price;
                        return sum + itemMSRP * item.quantity;
                      }, 0);
                      
                      return (
                        <div className="flex justify-between text-gray-600">
                          <span>{showMSRP ? 'Subtotal (MSRP)' : 'Subtotal'}</span>
                          <span style={{ fontFamily: 'JetBrains Mono' }}>
                            ${showMSRP ? currencyMSRP.toLocaleString() : actualTotal.toLocaleString()}
                          </span>
                        </div>
                      );
                    })()}
                    {/* Sale Savings - only show when sale discount is selected */}
                    {totalSavings > 0 && selectedDiscountSource === 'sale' && (
                      <div className="flex justify-between text-emerald-600">
                        <span className="flex items-center gap-1">
                          <Tag className="h-4 w-4" />
                          Sale Savings
                        </span>
                        <span style={{ fontFamily: 'JetBrains Mono' }}>-${totalSavings.toLocaleString()}</span>
                      </div>
                    )}
                    {/* Affiliate Discount - only show when affiliate is selected */}
                    {activeDiscount && (selectedDiscountSource === 'affiliate' || selectedDiscountSource === 'referral') && (
                      <div className="flex justify-between text-emerald-600">
                        <span className="flex items-center gap-1">
                          <Tag className="h-4 w-4" />
                          Affiliate Discount ({activeDiscount.affiliateName || 'Affiliate'})
                        </span>
                        <span style={{ fontFamily: 'JetBrains Mono' }}>-${activeDiscount.amount.toFixed(2)}</span>
                      </div>
                    )}
                    {/* Coupon Discount - show when coupon is selected */}
                    {activeDiscount && selectedDiscountSource === 'coupon' && (
                      <div className="flex justify-between text-[#003456]">
                        <span className="flex items-center gap-1">
                          <Tag className="h-4 w-4" />
                          {activeDiscount.label}
                        </span>
                        <span style={{ fontFamily: 'JetBrains Mono' }}>-${activeDiscount.amount.toFixed(2)}</span>
                      </div>
                    )}
                    
                    {/* Shipping Section */}
                    <div className="border-t border-gray-100 pt-3 mt-1">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-gray-600 flex items-center gap-1">
                          <Truck className="h-4 w-4" />
                          Shipping
                        </span>
                        {freeShippingThreshold > 0 && msrpTotal < freeShippingThreshold && (
                          <span className="text-xs text-green-600">
                            Free shipping over ${freeShippingThreshold}!
                          </span>
                        )}
                      </div>
                      
                      {/* Postal Code Input */}
                      <div className="mb-3">
                        <input
                          type="text"
                          placeholder="Enter postal/zip code"
                          value={postalCode}
                          onChange={(e) => setPostalCode(e.target.value.toUpperCase())}
                          className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003456]/20"
                          data-testid="postal-code-input"
                        />
                      </div>
                      
                      {/* Shipping Options */}
                      {shippingLoading ? (
                        <div className="text-sm text-gray-500 text-center py-2">Loading shipping options...</div>
                      ) : shippingOptions.length > 0 ? (
                        <div className="space-y-2" data-testid="shipping-options-list">
                          {/* Carrier (paid) options first */}
                          {shippingOptions.filter(o => !o.self_serve).map((option) => {
                            const isFree = freeShippingThreshold > 0 && msrpTotal >= freeShippingThreshold;
                            const displayCost = isFree ? 0 : option.cost;
                            return (
                              <label 
                                key={option.method}
                                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                                  selectedShipping?.method === option.method 
                                    ? 'border-[#003456] bg-[#003456]/5' 
                                    : 'border-gray-200 hover:border-gray-300'
                                }`}
                                data-testid={`shipping-option-${option.method}`}
                              >
                                <div className="flex items-center gap-3">
                                  <input
                                    type="radio"
                                    name="shipping"
                                    checked={selectedShipping?.method === option.method}
                                    onChange={() => setSelectedShipping(option)}
                                    className="text-[#003456] focus:ring-[#003456]"
                                  />
                                  <div>
                                    <p className="font-medium text-sm">{option.name}</p>
                                    <p className="text-xs text-gray-500">{option.days_min}-{option.days_max} business days</p>
                                  </div>
                                </div>
                                <span className={`font-medium ${isFree ? 'text-green-600' : ''}`} style={{ fontFamily: 'JetBrains Mono' }}>
                                  {displayCost === 0 ? 'FREE' : `$${displayCost.toFixed(2)}`}
                                </span>
                              </label>
                            );
                          })}

                          {/* Self-serve / no-shipping alternatives */}
                          {shippingOptions.some(o => o.self_serve) && (
                            <>
                              {shippingOptions.some(o => !o.self_serve) && (
                                <div className="flex items-center gap-2 pt-3 pb-1">
                                  <div className="flex-1 h-px bg-gray-200" />
                                  <span className="text-xs text-gray-500 font-medium uppercase tracking-wider">Or skip shipping</span>
                                  <div className="flex-1 h-px bg-gray-200" />
                                </div>
                              )}
                              {shippingOptions.filter(o => o.self_serve).map((option) => (
                                <label
                                  key={option.method}
                                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                                    selectedShipping?.method === option.method
                                      ? 'border-[#003456] bg-[#003456]/5'
                                      : 'border-gray-200 hover:border-gray-300'
                                  }`}
                                  data-testid={`shipping-option-${option.method}`}
                                >
                                  <input
                                    type="radio"
                                    name="shipping"
                                    checked={selectedShipping?.method === option.method}
                                    onChange={() => setSelectedShipping(option)}
                                    className="mt-1 text-[#003456] focus:ring-[#003456]"
                                  />
                                  <div className="flex-1">
                                    <div className="flex items-center justify-between">
                                      <p className="font-medium text-sm">{option.name}</p>
                                      <span className="text-green-600 font-medium text-sm" style={{ fontFamily: 'JetBrains Mono' }}>FREE</span>
                                    </div>
                                    {option.description && (
                                      <p className="text-xs text-gray-500 mt-1 leading-relaxed">{option.description}</p>
                                    )}
                                  </div>
                                </label>
                              ))}
                            </>
                          )}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">Enter postal code to see shipping options</p>
                      )}
                      
                      {/* Shipping Cost Summary */}
                      {selectedShipping && (
                        <div className="flex justify-between text-gray-600 mt-3 pt-2 border-t border-gray-100">
                          <span>{selectedShipping.name}</span>
                          <span style={{ fontFamily: 'JetBrains Mono' }}>
                            {shippingCost === 0 ? 'FREE' : `$${shippingCost.toFixed(2)}`}
                          </span>
                        </div>
                      )}
                      
                      {/* Shipping Warning — only if no postal code AND no self-serve option chosen */}
                      {!postalCode && !selectedShipping?.self_serve && (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="shipping-warning">
                          <div className="flex items-start gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-amber-800">
                              <p className="font-semibold">Enter a postal code for a shipping quote</p>
                              <p className="mt-1">Or choose <strong>Local Pickup</strong>, <strong>Arrange My Own Shipping</strong>, or <strong>Quote Shipping Later</strong> below to skip shipping at checkout.</p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Confirmation banner for self-serve choice */}
                      {selectedShipping?.self_serve && (
                        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="self-serve-banner">
                          <p className="text-sm text-blue-900">
                            <strong>{selectedShipping.name}</strong> selected. {selectedShipping.description}
                          </p>
                        </div>
                      )}
                    </div>
                    
                    {/* Tax (GST/HST/etc.) — driven by admin settings */}
                    {taxAmount > 0 && (
                      <div className="flex justify-between text-gray-600" data-testid="tax-line">
                        <span>{taxSettings.label} ({taxPercent}%{taxProvince ? ` · ${taxCountry}-${taxProvince}` : (taxCountry ? ` · ${taxCountry}` : '')})</span>
                        <span style={{ fontFamily: 'JetBrains Mono' }}>${taxAmount.toFixed(2)}</span>
                      </div>
                    )}

                    {hasSurcharge && surchargeAmount > 0 && (
                      <div className="flex justify-between text-gray-600" data-testid="credit-card-surcharge">
                        <span className="flex items-center gap-1">
                          <CreditCard className="h-4 w-4" />
                          Credit Card Fee ({surchargeSettings.percent}%)
                        </span>
                        <span style={{ fontFamily: 'JetBrains Mono' }}>${surchargeAmount.toFixed(2)}</span>
                      </div>
                    )}
                    {appliedStoreCredit && storeCreditAmount > 0 && (
                      <div className="flex justify-between text-blue-600" data-testid="store-credit-applied">
                        <span className="flex items-center gap-1">
                          <Tag className="h-4 w-4" />
                          Store Credit ({appliedStoreCredit.code})
                        </span>
                        <span style={{ fontFamily: 'JetBrains Mono' }}>-${storeCreditAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xl font-bold text-[#0a0203] pt-2 border-t border-gray-100">
                      <span>Total</span>
                      <span style={{ fontFamily: 'JetBrains Mono' }}>${finalTotal.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                    </div>
                  </div>

                  {hasSurcharge && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg text-xs text-blue-700 flex items-start gap-2" data-testid="surcharge-notice">
                      <Info className="h-4 w-4 flex-shrink-0 mt-0.5" />
                      <span>Switch to debit card{userRegion === 'CA' ? ', Interac' : userRegion === 'US' ? ', or ACH' : ', ACH, or Interac'} to avoid the {surchargeSettings.percent}% processing fee.</span>
                    </div>
                  )}

                  <Button
                    onClick={handleCheckout}
                    disabled={loading}
                    className="w-full mt-6 bg-[#003456] hover:bg-[#001d30] text-white py-6 text-lg btn-scale"
                    data-testid="checkout-btn"
                  >
                    {loading ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                    ) : (
                      <>
                        Checkout <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    Secure checkout powered by Stripe
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Footer />
      
      {/* Checkout Exit Intent Popup */}
      <CheckoutExitPopup 
        cartTotal={finalTotal}
        hasItems={items.length > 0}
        onApplyDiscount={handleApplyExitDiscount}
      />
    </div>
  );
};

export default Cart;
